import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { PageTransition } from '../../components/ui/PageTransition';
import { SEO } from '../../components/shared/SEO';
import { AnimatedSection } from '../../components/ui/AnimatedSection';
import { Modal } from '../../components/admin/Modal';
import { ProductForm } from '../../components/admin/ProductForm';
import { Link } from 'react-router-dom';

interface Product {
  _id: string;
  name: string;
  price: number;
  image_url: string;
  description: string[];
  execute: { type: string; text: string; }[];
  sort_id: number;
  created_at: string;
  updated_at: string;
}

interface Transaction {
  _id: string;
  username: string;
  amount: number;
  product_id: string;
  status: string;
  payment_id: string;
  payment_type: string;
  created_at: string;
  updated_at: string;
}

interface SendAction {
  _id: string;
  username: string;
  action: { type: string; text: string; }[];
  success: boolean;
  success_at: string;
}

interface PaginationData {
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

const ITEMS_PER_PAGE = 10;

export function AdminStore() {
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [sendActions, setSendActions] = useState<SendAction[]>([]);
  const [transactionsPagination, setTransactionsPagination] = useState<PaginationData | null>(null);
  const [actionsPagination, setActionsPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'products' | 'transactions' | 'actions'>('products');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(undefined);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [actionsPage, setActionsPage] = useState(1);

  const fetchTransactions = async (page: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_ENDPOINTS.admin.transactions}?page=${page}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch transactions');
      
      const data = await response.json();
      setTransactions(data.transactions);
      setTransactionsPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    }
  };

  const fetchSendActions = async (page: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(`${API_ENDPOINTS.admin.sendActions}?page=${page}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch send actions');
      
      const data = await response.json();
      setSendActions(data.actions);
      setActionsPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load send actions');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const productsRes = await fetch(API_ENDPOINTS.store.products);
      if (!productsRes.ok) throw new Error('Failed to fetch products');
      
      const productsData = await productsRes.json();
      setProducts(productsData);

      // Initial fetch of paginated data
      await Promise.all([
        fetchTransactions(1),
        fetchSendActions(1)
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions(transactionsPage);
    }
  }, [activeTab, transactionsPage]);

  useEffect(() => {
    if (activeTab === 'actions') {
      fetchSendActions(actionsPage);
    }
  }, [activeTab, actionsPage]);

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(API_ENDPOINTS.admin.product(productId), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    }
  };

  const handleAddProduct = () => {
    setSelectedProduct(undefined);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleProductFormSuccess = () => {
    setIsModalOpen(false);
    fetchData();
  };

  const handleResendAction = async (actionId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_ENDPOINTS.admin.resendAction(actionId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to resend action');
      }

      // Refresh the current page of actions
      await fetchSendActions(actionsPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend action');
    }
  };

  const PaginationControls = ({ 
    currentPage, 
    pagination,
    onPageChange 
  }: { 
    currentPage: number;
    pagination: PaginationData | null;
    onPageChange: (page: number) => void;
  }) => {
    if (!pagination) return null;

    return (
      <div className="flex justify-center space-x-2 mt-4 pb-4">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="px-3 py-1 text-gray-300">
          Page {currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!pagination.hasMore}
          className="px-3 py-1 rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <PageTransition>
      <SEO
        title="Store Management"
        description="KaizenMC Admin Panel - Store Management"
      />
      <div className="min-h-screen bg-gray-900 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-white">Store Management</h1>
            <Link
              to="/admin/home"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Back to Home
            </Link>
          </div>

          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="border-b border-gray-700">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('products')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'products'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Products
                </button>
                <button
                  onClick={() => setActiveTab('transactions')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'transactions'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Transactions
                </button>
                <button
                  onClick={() => setActiveTab('actions')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'actions'
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Send Actions
                </button>
              </nav>
            </div>

            {loading ? (
              <div className="text-center text-gray-400">Loading...</div>
            ) : error ? (
              <div className="text-red-500 text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                {error}
              </div>
            ) : (
              <div className="space-y-6">
                {activeTab === 'products' && (
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                      <h3 className="text-lg font-medium text-white">Products</h3>
                      <button
                        onClick={handleAddProduct}
                        className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                      >
                        Add Product
                      </button>
                    </div>
                    <div className="border-t border-gray-700">
                      <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Price</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Sort ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                          {products.map((product) => (
                            <tr key={product._id} className="hover:bg-gray-700/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.price}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.sort_id}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                <button
                                  onClick={() => handleEditProduct(product)}
                                  className="text-primary-400 hover:text-primary-300 mr-4"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteProduct(product._id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === 'transactions' && (
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg font-medium text-white">Transactions</h3>
                    </div>
                    <div className="border-t border-gray-700">
                      <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Username</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Payment Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                          {transactions.map((transaction) => (
                            <tr key={transaction._id} className="hover:bg-gray-700/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{transaction.username}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{transaction.amount}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  transaction.status === 'PAID'
                                    ? 'bg-green-100 text-green-800'
                                    : transaction.status === 'PENDING'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {transaction.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{transaction.payment_type}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                {new Date(transaction.created_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <PaginationControls
                        currentPage={transactionsPage}
                        pagination={transactionsPagination}
                        onPageChange={setTransactionsPage}
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'actions' && (
                  <div className="bg-gray-800 rounded-lg overflow-hidden">
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg font-medium text-white">Send Actions</h3>
                    </div>
                    <div className="border-t border-gray-700">
                      <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700/50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Username</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                          {sendActions.map((action) => (
                            <tr key={action._id} className="hover:bg-gray-700/50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{action.username}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    action.success
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {action.success ? 'Success' : 'Failed'}
                                  </span>
                                  {action.success && (
                                    <button
                                      onClick={() => handleResendAction(action._id)}
                                      className="text-primary-400 hover:text-primary-300 text-sm font-medium"
                                    >
                                      Resend
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-white">
                                <ul className="list-disc list-inside">
                                  {action.action.map((a, index) => (
                                    <li key={index}>{a.text}</li>
                                  ))}
                                </ul>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                {new Date(action.success_at).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <PaginationControls
                        currentPage={actionsPage}
                        pagination={actionsPagination}
                        onPageChange={setActionsPage}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedProduct ? 'Edit Product' : 'Add Product'}
      >
        <ProductForm
          product={selectedProduct}
          onSuccess={handleProductFormSuccess}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>
    </PageTransition>
  );
} 